import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import * as RadixContextMenu from '@radix-ui/react-context-menu';
import styles from './Menus.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeContextMenu,
  selectArrayOfSelectedContacts,
  selectContextMenu,
  selectIsMultiselectView,
  selectSelectedContact,
  selectSelectedContactBatch,
} from '@repo/redux/actions/ui';
import { buildContactActions } from '@views/CommandBar/actionsBySection/contactActions';
import { Set } from 'immutable';
import { useLocation, useNavigate } from 'react-router-dom';
import buildContactBatchActions from '@views/CommandBar/actionsBySection/contactBatchActions';
import { ExpandingMenu } from '@repo/components/menus';
import { selectActiveGroupView } from '@repo/redux/actions/groupView';
import { useAuthContext } from '@repo/hooks';
import { buildArchiveActions } from '@views/CommandBar/actionsBySection/archivedContactActions';
import { selectArchiveView } from '@repo/redux/reducers/ui/archiveView';
import { buildDuplicateActions } from '@views/CommandBar/actionsBySection/duplicateActions';
import { selectDuplicatesView } from '@repo/redux/reducers/ui/duplicatesView';

const px = (x: number) => `${x}px`;

export default function ContextMenu() {
  const { isOpen } = useSelector(selectContextMenu);
  return isOpen && <ContextMenuDisplay />;
}

export function ContextMenuDisplay() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { coords, viewportDimensions, target } = useSelector(selectContextMenu);
  const selectedContact = useSelector(selectSelectedContact);
  const selectedContactBatch = useSelector(selectSelectedContactBatch);
  const selectedContactArray = useSelector(selectArrayOfSelectedContacts);
  const groupView = useSelector(selectActiveGroupView);
  const archiveView = useSelector(selectArchiveView);
  const duplicatesView = useSelector(selectDuplicatesView);
  const isMultiselectView = useSelector(selectIsMultiselectView);
  const { contactIsRestricted, getEntitlementValue, contactIsSelf } = useAuthContext();
  const [menuOptions, setMenuOptions] = useState([]);

  const closeMenu = useCallback(() => dispatch(closeContextMenu), []);
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    closeMenu();
  }, []);

  // Determine what quadrant of the UI user is in to determine menu placement
  const menuStyle = useMemo(() => {
    const { x, y } = coords;
    const { width, height } = viewportDimensions;
    const relativeX = x / width;
    const relativeY = y / height;
    let menuStyle: CSSProperties = { left: px(x), top: px(y) };

    if (relativeX > 0.5 && relativeY <= 0.5) {
      menuStyle = { right: px(width - x), top: px(y) };
    } else if (relativeX <= 0.5 && relativeY > 0.5) {
      menuStyle = { left: px(x), bottom: px(height - y) };
    } else if (relativeX > 0.5 && relativeY > 0.5) {
      menuStyle = { right: px(width - x), bottom: px(height - y) };
    }

    return menuStyle;
  }, [coords, viewportDimensions]);

  useEffect(() => {
    let menuOptions = [];

    if (target == 'ArchivedContact') {
      menuOptions = buildArchiveActions(archiveView, dispatch);
    } else if (target === 'Duplicate') {
      menuOptions = buildDuplicateActions(duplicatesView, dispatch);
    } else if (selectedContactBatch.size < 2) {
      menuOptions = buildContactActions(
        { selectedContact, selectedContactBatch: Set(), groupView, location },
        dispatch,
        navigate,
        contactIsRestricted,
        getEntitlementValue,
        contactIsSelf,
      );
    } else {
      // do we need the last arg? what is it practically used for?
      menuOptions = buildContactBatchActions(
        { selectedContactBatch, groupView, selectedContactArray, isMultiselectView },
        dispatch,
        contactIsRestricted,
        'List',
      );
    }

    if (menuOptions.length === 0) {
      dispatch(closeContextMenu);
    } else {
      setMenuOptions(menuOptions);
    }
  }, [selectedContact, selectedContactBatch, navigate]);

  useEffect(() => {
    window.addEventListener('resize', closeMenu);
    return () => window.removeEventListener('resize', closeMenu);
  }, []);

  return (
    <div
      className={cn(styles.contextMenuOverlay, styles.open)}
      onClick={closeMenu}
      onContextMenu={handleContextMenu}
    >
      <RadixContextMenu.Root onOpenChange={(open) => !open && closeMenu()}>
        <ExpandingMenu
          options={menuOptions}
          isOpen={menuOptions.length > 0}
          menuStyle={menuStyle}
          menuClassName={styles.contextMenu}
          variant="context"
        />
      </RadixContextMenu.Root>
    </div>
  );
}
